import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Check, Beaker, ShieldCheck } from "lucide-react";
import PageHero from "../components/PageHero";
import PinkwaterLabel from "../components/PinkwaterLabel";
import { cleaningContent, cleaningBrands, company } from "../mock";

export default function CleaningSolutions() {
  const [active, setActive] = useState(cleaningBrands[0].id);
  const activeBrand = cleaningBrands.find((b) => b.id === active);

  return (
    <div>
      <PageHero kicker="Cleaning Solutions" title={cleaningContent.header} image={cleaningContent.headerImage} />

      <section className="py-20 bg-white">
        <div className="mx-auto max-w-[900px] px-6">
          <h2 className="font-heading text-2xl md:text-3xl text-[#1a2540] leading-snug mb-8 text-center">
            {cleaningContent.lead}
          </h2>
          <div className="w-14 h-[3px] bg-[#1b75bc] mx-auto mb-10" />
          {cleaningContent.paragraphs.map((p, i) => (
            <p key={i} className="text-[#6a6a6a] leading-relaxed mb-6">
              {p}
            </p>
          ))}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="font-heading uppercase tracking-[0.18em] text-sm text-white bg-[#1b75bc] px-8 py-4 hover:bg-[#155e97] transition-colors duration-300"
            >
              Contact to Order
            </Link>
            <a
              href={`tel:${company.phoneRaw}`}
              className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.18em] text-sm text-[#1a2540] border border-[#1a2540] px-8 py-4 hover:bg-[#1a2540] hover:text-white transition-colors duration-300"
            >
              <Phone size={16} /> {company.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Brand tabs */}
      <section className="py-20 bg-[#f6f8fa]">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="text-center mb-12">
            <p className="font-heading uppercase tracking-[0.35em] text-[#1b75bc] text-sm mb-3">Our Brands</p>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540]">
              Product Lines
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {cleaningBrands.map((brand) => (
              <button
                key={brand.id}
                onClick={() => setActive(brand.id)}
                aria-label={brand.name}
                className={`flex items-center justify-center h-24 w-40 px-5 bg-white rounded-xl border transition-all duration-300 ${
                  active === brand.id
                    ? "border-[#1b75bc] shadow-[0_14px_30px_-14px_rgba(27,117,188,0.5)] ring-1 ring-[#1b75bc]/30 -translate-y-0.5"
                    : "border-gray-200 hover:border-[#1b75bc] hover:shadow-md hover:-translate-y-0.5 opacity-70 hover:opacity-100"
                }`}
              >
                {brand.logo ? (
                  <img
                    src={brand.logo}
                    alt={`${brand.name} logo`}
                    className="max-h-14 max-w-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <PinkwaterLabel size="md" />
                )}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-[0_20px_45px_-25px_rgba(15,41,74,0.35)] border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8 pb-6 border-b border-gray-100">
              {activeBrand.logo ? (
                <img
                  src={activeBrand.logo}
                  alt={`${activeBrand.name} logo`}
                  className="h-16 w-auto object-contain"
                />
              ) : (
                <PinkwaterLabel size="lg" />
              )}
              <h3 className="font-heading text-2xl uppercase tracking-[0.06em] text-[#1a2540]">
                {activeBrand.name}
              </h3>
            </div>

            {activeBrand.applications ? (
              /* Rich content (Pinkwater) */
              <div>
                <h4 className="font-heading text-lg text-[#1a2540] mb-3">
                  {activeBrand.headline}
                </h4>
                <p className="text-[#6a6a6a] leading-relaxed mb-10 max-w-4xl">
                  {activeBrand.description}
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
                  <div>
                    <div className="flex items-center gap-2.5 mb-5">
                      <Beaker size={20} className="text-[#1b75bc]" />
                      <h4 className="font-heading text-base uppercase tracking-[0.08em] text-[#1a2540]">
                        Applications Include
                      </h4>
                    </div>
                    <ul className="space-y-3">
                      {activeBrand.applications.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <Check size={16} className="text-[#1b75bc] mt-0.5 shrink-0" />
                          <span className="text-sm text-[#4a4a4a] leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="flex items-center gap-2.5 mb-5">
                      <ShieldCheck size={20} className="text-[#1b75bc]" />
                      <h4 className="font-heading text-base uppercase tracking-[0.08em] text-[#1a2540]">
                        Key Benefits
                      </h4>
                    </div>
                    <ul className="space-y-3">
                      {activeBrand.benefits.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <Check size={16} className="text-[#1b75bc] mt-0.5 shrink-0" />
                          <span className="text-sm text-[#4a4a4a] leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-[#f6f8fa] rounded-xl border-l-4 border-[#1b75bc] p-6">
                  <p className="text-sm text-[#4a4a4a] leading-relaxed italic">
                    {activeBrand.closing}
                  </p>
                </div>
              </div>
            ) : (
              /* Simple product list (janitorial brands) */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                {activeBrand.products.map((prod) => (
                  <div key={prod} className="flex items-center gap-3 py-2 border-b border-gray-100">
                    <Check size={16} className="text-[#1b75bc] shrink-0" />
                    <span className="text-sm text-[#4a4a4a]">{prod}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
